import React, { useState } from 'react';
import { adminAPI } from '../../services/api';
import type { QueueLoginRole } from '../../utils/queueLoginRole';
import { setQueueLoginRole } from '../../utils/queueLoginRole';
import { getStoredPhoneExtension } from '../../utils/screenPopAccess';

interface QueueRolePromptModalProps {
  onConfirmed: (role: QueueLoginRole) => void;
}

function formatQueueApiError(err: any): string {
  const detail = err?.response?.data?.detail;
  const raw = typeof detail === 'string' ? detail : '';
  const lowered = raw.toLowerCase();
  if (
    lowered === 'not authenticated' ||
    lowered.includes('not authenticated') ||
    err?.response?.status === 401
  ) {
    return 'نشست پنل منقضی شده یا توکن ارسال نشده است. یک‌بار خارج شوید و دوباره وارد شوید.';
  }
  if (
    lowered.includes('ami') ||
    lowered.includes('authentication') ||
    raw.includes('مرکز تلفن') ||
    raw.includes('Issabel') ||
    raw.includes('manager.conf')
  ) {
    return raw || 'ورود به صف مرکز تلفن ناموفق بود. تنظیمات AMI و دسترسی write=agent را بررسی کنید.';
  }
  return raw || 'خطا در ثبت انتخاب نقش صف';
}

const QueueRolePromptModal: React.FC<QueueRolePromptModalProps> = ({ onConfirmed }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allowContinueAnyway, setAllowContinueAnyway] = useState(false);

  const finish = (role: QueueLoginRole) => {
    setQueueLoginRole(role);
    onConfirmed(role);
  };

  const handleChoose = async (role: QueueLoginRole) => {
    setSaving(true);
    setError(null);
    setAllowContinueAnyway(false);
    try {
      const extension = getStoredPhoneExtension() || undefined;
      if (role === 'booking') {
        const { data } = await adminAPI.joinQueueLogin(extension);
        if (data.feature_enabled && !data.in_queue) {
          setError(data.message || 'افزودن به صف تأیید نشد.');
          setAllowContinueAnyway(true);
          setSaving(false);
          return;
        }
      } else {
        // اگر از قبل در صف مانده، خارج شود — شکست AMI نباید ورود به پنل را ببندد
        try {
          await adminAPI.leaveQueueLogin(extension);
        } catch {
          // ignore
        }
      }
      finish(role);
    } catch (err: any) {
      setError(formatQueueApiError(err));
      // برای نوبت‌دهی هم اجازه بده وارد پنل شود تا کار متوقف نشود
      setAllowContinueAnyway(role === 'booking');
      if (role === 'outside') {
        finish('outside');
        return;
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4">
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        style={{ direction: 'rtl' }}
      >
        <div className="mb-5">
          <h3 className="text-lg font-bold text-gray-900">نوع فعالیت شما چیست؟</h3>
          <p className="mt-2 text-sm leading-7 text-gray-600">
            اگر نوبت‌دهی می‌کنید به صف تماس وصل می‌شوید. در غیر این صورت خارج از صف می‌مانید و تلفن از صف
            زنگ نمی‌خورد.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid gap-3">
          <button
            type="button"
            disabled={saving}
            onClick={() => handleChoose('booking')}
            className="rounded-xl bg-teal-700 px-4 py-3.5 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-60"
          >
            نوبت‌دهی
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => handleChoose('outside')}
            className="rounded-xl border border-gray-300 bg-white px-4 py-3.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-60"
          >
            خارج از صف
          </button>
          {allowContinueAnyway && (
            <button
              type="button"
              disabled={saving}
              onClick={() => finish('booking')}
              className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
            >
              فعلاً بدون صف وارد پنل شو
            </button>
          )}
        </div>

        {saving && (
          <p className="mt-3 text-center text-xs text-gray-500">در حال ثبت...</p>
        )}
      </div>
    </div>
  );
};

export default QueueRolePromptModal;
